import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { companiesApi } from 'src/api/companiesApi';
import { parseSubscriptionsList, subscriptionsApi } from 'src/api/subscriptionsApi';
import SubHeader from 'src/components/SubHeader';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { CustomButton2 } from 'src/components/widgets/buttons/CustomButton2';
import SearchBar from 'src/components/widgets/searchBar/SearchBar';
import { companiesColumns } from 'src/constants/columns';
import useGenericStyles from 'src/styles/useGenericStyles';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import CreateClientWizard from './create-client-wizard/CreateClientWizard';
import {
    clearPendingClientSubscription,
    getPendingClientSubscriptionForUser,
} from './create-client-wizard/pendingClientSubscription';
import { getClientDetailsHref } from './company-details/clientDetailsTabs';

type ResumeClient = {
    organizationId: string;
    organizationName: string;
};

export default function Companies() {
    const theme = useTheme();
    const classes = useGenericStyles();
    const { userData } = useUserProfile();
    const navigate = useNavigate();

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [sort, setSort] = useState({});
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);
    const [resumeClient, setResumeClient] = useState<ResumeClient | null>(null);

    const fetchChildCompanies = async (currentPage = 0, currentSearch = '') => {
        setLoading(true);
        try {
            const response = await companiesApi.getChildCompaniesByParentId({
                parentId: userData?.organization?.id,
                page: currentPage,
                pageSize,
                search: currentSearch
            });
            if (response?.data) {
                setData(response.data?.data);
                setTotalCount(response.data?.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching child companies:', error);
            setData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        setPage(0);
    };

    const handlePage = (temp: number) => {
        setPage(temp);
    };
    const handlePageSize = (temp: number) => {
        setPageSize(temp);
    };

    const handleSort = (col: any) => setSort(col);

    const handleRowClick = (row) => {
        navigate(getClientDetailsHref(row.id));
    };

    const handleCloseWizard = () => {
        setIsCreateClientOpen(false);
        if (!getPendingClientSubscriptionForUser(userData)) {
            setResumeClient(null);
        }
    };

    useEffect(() => {
        if (userData?.organization?.id) {
            fetchChildCompanies(page, search);
        }
    }, [page, search, pageSize, userData]);

    useEffect(() => {
        if (!userData?.organization?.id) return;

        const pending = getPendingClientSubscriptionForUser(userData);
        if (!pending) return;

        let cancelled = false;
        subscriptionsApi
            .getSubscriptions({ clientId: pending.organizationId, page: 1, size: 1 })
            .then((response) => {
                if (cancelled) return;
                const { items } = parseSubscriptionsList(response);
                if (items.length > 0) {
                    clearPendingClientSubscription();
                    setResumeClient(null);
                    return;
                }
                setResumeClient({
                    organizationId: pending.organizationId,
                    organizationName: pending.organizationName,
                });
                setIsCreateClientOpen(true);
            })
            .catch(() => {
                if (cancelled) return;
                setResumeClient({
                    organizationId: pending.organizationId,
                    organizationName: pending.organizationName,
                });
                setIsCreateClientOpen(true);
            });

        return () => {
            cancelled = true;
        };
    }, [userData]);

    return (
        <>
            <Helmet>
                <title>Mindtime User Portal | Clients</title>
            </Helmet>
            <SubHeader title="Clients" />
            <Container maxWidth={false} className={classes.container}>
                <Box
                    sx={{
                        marginBottom: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <SearchBar
                        value={search}
                        onSearch={handleSearch}
                        placeholderData="Search..."
                    />
                    <CustomButton2
                        backgroundColor={theme.palette.primary.main}
                        className={classes.button}
                        disabled={Boolean(resumeClient)}
                        onClick={() => {
                            setResumeClient(null);
                            setIsCreateClientOpen(true);
                        }}>
                        <AddIcon
                            sx={{
                                marginRight: '5px',
                            }}
                        />
                        Create New Client Company
                    </CustomButton2>
                </Box>

                <CustomizedTable
                    heading="All Clients"
                    loading={loading}
                    columns={companiesColumns}
                    page={page}
                    handleOnClick={handleRowClick}
                    limit={pageSize}
                    handleLimit={handlePageSize}
                    handlePage={handlePage}
                    data={data}
                    totalRecords={totalCount}
                    handleSort={handleSort}
                    sort={sort}
                    navigateToKey
                    selectRowData
                    customizableColumns
                />
            </Container>
            <CreateClientWizard
                open={isCreateClientOpen}
                resumeClient={resumeClient}
                onClose={handleCloseWizard}
                onCreated={() => fetchChildCompanies(page, search)}
            />
        </>
    );
}
